import {matchPath, useLocation, useNavigation} from '@github-ui/react-router'
import type {Location} from '@github-ui/react-router'
import {announce} from '@github-ui/aria-live'
import type {FilterRef} from '@github-ui/filter'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {shouldDisplayBestMatchDefault} from '@github-ui/issue-query-free-text'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQueryClient} from '@github-ui/react-query'
import {isActiveIssueFilterAvailable, selectIssueStateCounts} from '@github-ui/issues-shared/issue-state-filter'
import {useRepositoryFilterActions} from '@github-ui/issues-shared/repository-filter-actions'
import {FirstTimeContributionBannerDisplay} from '@github-ui/issues-shared/first-time-contribution-banner'
import {MoreResultsAvailableBanner} from '@github-ui/issues-shared/more-results-available-banner'
import {VALUES} from '@github-ui/issues-shared/values'
import {CachePreheatContextProvider} from '@github-ui/preheat-issues-manager/cache-preheat-context'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {RepoIssuesDashboardPageInfoSchema} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {ModifierKeys, useKeyPress} from '@github-ui/use-key-press'
import {reactFetch} from '@github-ui/verified-fetch'
import {Banner, Button, Stack} from '@primer/react'
import {memo, startTransition, useCallback, useEffect, useMemo, useRef, useState, type RefObject} from 'react'

import {useIssuesIndexResultsForRoute} from '../hooks/use-issues-index-results'
import {ISSUES_RESULTS_SPACING_STYLE, IssuesListSurface} from '../components/IssuesListSurface'
import {IssueRowsPreheater} from '../hooks/issue-rows-preheater'
import {RepoIssuesBulkJobInfo} from '../components/RepoIssuesBulkJobInfo'
import {PinnedIssuesStrip} from '../components/pinned-issues/PinnedIssuesStrip'
import {createRepoRowNodeLoader, shouldFetchReactions} from '../hooks/repo-issues-row-node'
import {useRepoIssuesBulkActionState} from '../hooks/use-repo-issues-bulk-action-state'
import {useRepoIssuesSearchMetrics} from '../hooks/use-repo-issues-search-metrics'
import {useIssuesDashboardLiveUpdates} from '../hooks/use-issues-dashboard-live-updates'
import {useRowNodeOverlay} from '../hooks/use-row-node-overlay'
import {useIssuesListControls} from '../hooks/use-issues-list-controls'
import {isPullRequestsOnlyQuery} from '../helpers/is-explicit-issue-query'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'
import {useOptimisticRepoIssuesBulkUpdates} from '../helpers/repo-issues-bulk-optimistic-update'
import {
  issuesIndexQueryResolver,
  REPO_INDEX_CLOSED_QUERY,
  REPO_INDEX_DEFAULT_QUERY,
} from '../helpers/issues-index-query-resolver'
import {ssoOrgsInQuery} from '../helpers/sso-orgs-in-query'
import {IssuesIndexSearch} from './IssuesIndexSearch'
import {ISSUES_INDEX_PATHS, issuesIndexContentRoute, issuesLabelContentRoute} from './issues-index-content-route'
import {issuesLayoutRoute} from './issues-layout-route'
import {preloadIssueViewer} from './issue-viewer-component'
import {IssuesSidebarPreheater, useRenderSidebar} from './render-issues-sidebar'

const REFRESH_ERROR_ANNOUNCEMENT = 'Unable to refresh issues. Showing previously loaded results.'
const FOCUS_FILTER_KEYS = ['/']
const ISSUES_CONTENT_STYLE = {
  padding: 'var(--base-size-24)',
} as const

function isSameLocation(a: Pick<Location, 'pathname' | 'search'>, b: Pick<Location, 'pathname' | 'search'>): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

function isIssuesIndexPath(pathname: string): boolean {
  return Object.values(ISSUES_INDEX_PATHS).some(path => matchPath(path, pathname))
}

function focusSearchInput(searchInputRef: RefObject<HTMLInputElement | null>) {
  searchInputRef.current?.focus()
}

/**
 * Repo Issues#index content: a repo analog of `IssuesDashboardSurfaceContent` that
 * reuses the shared `IssuesList`, pagination, skeleton/blankslate, and a repo-scoped
 * `<Filter>` to render the mixed (issues + PRs) result list.
 */
export function IssuesIndexContent() {
  return (
    <CachePreheatContextProvider>
      <IssuesIndexContentWithPreheating />
    </CachePreheatContextProvider>
  )
}

function IssuesIndexContentWithPreheating() {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const filterRef = useRef<FilterRef>(null)
  const location = useLocation()
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute
  const {owner, repo} = useRouteParams(contentRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const renderSidebar = useRenderSidebar(owner, repo, layoutPayload)
  const searchParams = new URLSearchParams(location.search)
  const resolvedQuery = issuesIndexQueryResolver({pathname: location.pathname, searchParams})
  const showPinnedStrip =
    (resolvedQuery === REPO_INDEX_DEFAULT_QUERY || resolvedQuery === REPO_INDEX_CLOSED_QUERY) &&
    Number(searchParams.get('page') || 1) <= 1

  const keyboardShortcutsEnabled = useMemo(() => areCharacterKeyShortcutsEnabled(), [])
  const focusFilter = (event: KeyboardEvent) => {
    if (!keyboardShortcutsEnabled) return

    const input = searchInputRef.current
    const currentFilter = filterRef.current
    if (input && currentFilter) {
      currentFilter.setValueAndOpenSuggestions(input.value)
      event.preventDefault()
    }
  }

  useKeyPress(FOCUS_FILTER_KEYS, focusFilter, {[ModifierKeys.metaKey]: true})
  useKeyPress(FOCUS_FILTER_KEYS, focusFilter, {[ModifierKeys.ctrlKey]: true})

  return (
    <>
      {contentRoute === issuesIndexContentRoute ? <IssuesSidebarPreheater owner={owner} repo={repo} /> : null}
      <BaseAnalyticsWrapper category="Repository Issues Index" appName="issues">
        <RepoIssuesPageLayout
          renderSidebar={renderSidebar}
          initialExpanded={layoutPayload?.sidebarExpanded}
          contentPadding="none"
          contentStyle={ISSUES_CONTENT_STYLE}
        >
          <Stack direction="vertical" gap="none">
            <IssuesIndexSearch
              inputRef={searchInputRef}
              filterRef={filterRef}
              beforeFilter={
                showPinnedStrip ? (
                  <PinnedIssuesStrip
                    owner={owner}
                    repo={repo}
                    pinnedIssues={layoutPayload?.pinnedIssues}
                    fallbackFocusRef={searchInputRef}
                  />
                ) : null
              }
            />
            <ErrorBoundary
              boundaryName="IssuesIndexContent"
              fallback={
                <div style={ISSUES_RESULTS_SPACING_STYLE}>
                  <SharedDashboardBlankslate dashboardType="issues" variant="error" />
                </div>
              }
            >
              <IssuesIndexPageContentWithNavigation
                searchInputRef={searchInputRef}
                pathname={location.pathname}
                search={location.search}
                owner={owner}
                repo={repo}
                isLabelsRoute={contentRoute === issuesLabelContentRoute}
                layoutPayload={layoutPayload}
              />
            </ErrorBoundary>
          </Stack>
        </RepoIssuesPageLayout>
      </BaseAnalyticsWrapper>
    </>
  )
}

function IssuesIndexPageContentWithNavigation({
  searchInputRef,
  pathname,
  search,
  owner,
  repo,
  isLabelsRoute,
  layoutPayload,
}: {
  searchInputRef: RefObject<HTMLInputElement | null>
  pathname: string
  search: string
  owner: string
  repo: string
  isLabelsRoute: boolean
  layoutPayload: ReturnType<typeof useRouteQuery<typeof issuesLayoutRoute, 'mainQuery'>>['data']
}) {
  const navigation = useNavigation()
  const contentRoute = isLabelsRoute ? issuesLabelContentRoute : issuesIndexContentRoute
  const mainQuery = useSuspenseRouteQuery(contentRoute, 'mainQuery')
  const secondaryQuery = useRouteQuery(contentRoute, 'secondary')
  const routeResults = useIssuesIndexResultsForRoute({pathname, search, owner, repo})
  const isNavigating = Boolean(
    navigation.location &&
    isIssuesIndexPath(navigation.location.pathname) &&
    !isSameLocation(navigation.location, {pathname, search}),
  )

  return (
    <MemoizedIssuesIndexPageContent
      isNavigating={isNavigating}
      searchInputRef={searchInputRef}
      pathname={pathname}
      search={search}
      owner={owner}
      repo={repo}
      layoutPayload={layoutPayload}
      mainData={mainQuery.data}
      mainDataUpdatedAt={mainQuery.dataUpdatedAt}
      mainIsRefetchError={mainQuery.isRefetchError}
      refetchMain={mainQuery.refetch}
      secondaryData={secondaryQuery.data}
      secondaryQueryKey={secondaryQuery.queryKey}
      refetchSecondary={secondaryQuery.refetch}
      routeResults={routeResults}
    />
  )
}

function IssuesIndexPageContent({
  isNavigating,
  searchInputRef,
  pathname,
  search,
  owner,
  repo,
  layoutPayload,
  mainData,
  mainDataUpdatedAt,
  mainIsRefetchError,
  refetchMain,
  secondaryData: secondaryQueryData,
  secondaryQueryKey,
  refetchSecondary,
  routeResults,
}: {
  isNavigating: boolean
  searchInputRef: RefObject<HTMLInputElement | null>
  pathname: string
  search: string
  owner: string
  repo: string
  layoutPayload: ReturnType<typeof useRouteQuery<typeof issuesLayoutRoute, 'mainQuery'>>['data']
  mainData: ReturnType<typeof useSuspenseRouteQuery<typeof issuesIndexContentRoute, 'mainQuery'>>['data']
  mainDataUpdatedAt: number
  mainIsRefetchError: boolean
  refetchMain: ReturnType<typeof useSuspenseRouteQuery<typeof issuesIndexContentRoute, 'mainQuery'>>['refetch']
  secondaryData: ReturnType<typeof useRouteQuery<typeof issuesIndexContentRoute, 'secondary'>>['data']
  secondaryQueryKey: ReturnType<typeof useRouteQuery<typeof issuesIndexContentRoute, 'secondary'>>['queryKey']
  refetchSecondary: ReturnType<typeof useRouteQuery<typeof issuesIndexContentRoute, 'secondary'>>['refetch']
  routeResults: ReturnType<typeof useIssuesIndexResultsForRoute>
}) {
  const issueListRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const data = mainData
  const {currentPage, fetchResults, fetchedQuery, getPageHref, getQueryHref} = routeResults
  const scopedRepository = useMemo(() => ({owner, name: repo}), [owner, repo])
  const fetchReactions = shouldFetchReactions(fetchedQuery)
  const rowNodeLoader = useMemo(
    () => createRepoRowNodeLoader(owner, repo, fetchReactions),
    [owner, repo, fetchReactions],
  )
  const rowNodeQueryScope = `${owner}/${repo}:fetch-reactions=${fetchReactions}`

  const applySectionFilter = useCallback(
    (query: string) => {
      startTransition(() => {
        fetchResults({q: query})
      })
    },
    [fetchResults],
  )
  const filterActions = useRepositoryFilterActions({
    repo: scopedRepository,
    query: fetchedQuery,
    applySectionFilter,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
  })

  const {isBrowser} = useRuntimeEnvironment()
  // Deferred data is not seeded by SSR, so a fast fetch must not change the hydration snapshot.
  const secondaryData = isBrowser ? secondaryQueryData?.payload?.issuesIndexSecondaryData : undefined
  const secondaryResults = secondaryData?.results
  const listRows = useMemo(
    () => mergeDeferredIssues(repoIssuesToListItems(data.results ?? []), secondaryResults ?? []),
    [data.results, secondaryResults],
  )
  // Per-row live-update overlay: an Alive event refreshes just that row via the
  // repo `_rows` endpoint instead of refetching the list.
  const rowNodeIssues = useRowNodeOverlay({
    rows: listRows,
    listUpdatedAt: mainDataUpdatedAt,
    loadRowNode: rowNodeLoader.loadRowNode,
    queryKeyScope: rowNodeQueryScope,
  })
  const issues = useOptimisticRepoIssuesBulkUpdates(rowNodeIssues)
  const liveUpdates = useIssuesDashboardLiveUpdates(listRows, rowNodeQueryScope)

  // Labelling only. github/github#423765 orders a relevance search newest-first when the backend
  // has no text left to rank after extracting qualifiers, so the label has to follow the same
  // rule. The request query is unaffected and may still carry `sort:relevance-desc`.
  const defaultSortingKey = shouldDisplayBestMatchDefault(layoutPayload?.bestMatchSortEnabled, fetchedQuery)
    ? 'relevance'
    : 'created'
  const issueStatusSearchOptions = layoutPayload && {
    statuses: layoutPayload.issueStatusNames,
    categories: layoutPayload.issueStatusCategoryKeys,
  }
  // The content response's own counts aren't used here; the secondary request counts.
  const pageInfo = create(RepoIssuesDashboardPageInfoSchema, {
    currentPage: data.pageInfo?.currentPage ?? 1,
    totalPages: data.pageInfo?.totalPages ?? 1,
    totalCount: data.pageInfo?.totalCount ?? 0,
    ...selectIssueStateCounts(fetchedQuery, isActiveIssueFilterAvailable(issueStatusSearchOptions), {
      deferred: secondaryData?.pageInfo,
    }),
  })
  const repositoryId = layoutPayload && 'repositoryId' in layoutPayload ? layoutPayload.repositoryId : ''
  const viewerCanBulkUpdate = layoutPayload && 'canBulkUpdate' in layoutPayload && layoutPayload.canBulkUpdate === true
  const refreshIssues = async () => {
    // A refetch otherwise reuses an initial deferred request that started before the write.
    if (secondaryQueryKey) {
      await queryClient.cancelQueries({queryKey: secondaryQueryKey, exact: true})
    }
    const [main, secondary] = await Promise.all([refetchMain(), refetchSecondary()])
    return main.isError ? main : secondary
  }
  const {
    bulkJobId,
    clearBulkJobId,
    selection,
    notificationStateCommands,
    bulkActionError,
    clearBulkActionError,
    onBulkJobCompleted,
    onBulkJobError,
  } = useRepoIssuesBulkActionState({
    owner,
    repo,
    currentPage,
    query: fetchedQuery,
    repositoryId,
    currentUserId: layoutPayload?.currentUser?.id ?? '',
    viewerCanBulkUpdate,
    totalCount: pageInfo.totalCount,
    issues,
    hasProjectsEnabled: layoutPayload?.hasProjectsEnabled ?? false,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
    refreshIssues,
  })
  const {trackRowSelection, trackIssueNavigation} = useRepoIssuesSearchMetrics(
    new URLSearchParams(search).get('q'),
    data.issueSearchType,
    bulkJobId,
  )

  const onlyPrs = isPullRequestsOnlyQuery(fetchedQuery)
  const itemsLabel = onlyPrs ? 'pull requests' : 'issues'
  const maxItems = VALUES.maxIssuesListItems(onlyPrs, Boolean(layoutPayload?.currentUser))
  const showMoreResultsAvailable = Boolean(
    pageInfo && pageInfo.currentPage === pageInfo.totalPages && pageInfo.totalCount > maxItems,
  )
  const cappedResultsAnnouncement = showMoreResultsAvailable
    ? `Showing the first ${maxItems} ${itemsLabel}. To view additional ${itemsLabel}, please refine your search.`
    : undefined
  // Persisted density + screen-reader announcements, shared with the dashboard. Density is
  // server-seeded from the layout payload so the list renders at the persisted density on first paint.
  const {compactMode, onCompactModeChange, emptyListState} = useIssuesListControls({
    initialCompactMode: layoutPayload?.compactMode ?? false,
    compactModePreferenceKey: 'dashboard_surface_compact_mode',
    isFetching: isNavigating,
    currentPage,
    fetchedQuery,
    defaultSortingKey,
    totalCount: pageInfo.totalCount,
    totalPages: pageInfo.totalPages,
    errorMessage: data.error || undefined,
    additionalAnnouncement: cappedResultsAnnouncement,
  })

  const error = data.error || null
  const orgsRequiringSso = useMemo(
    () => ssoOrgsInQuery(fetchedQuery, layoutPayload?.ssoOrganizationLogins ?? []),
    [fetchedQuery, layoutPayload?.ssoOrganizationLogins],
  )
  useEffect(() => {
    if (mainIsRefetchError) announce(REFRESH_ERROR_ANNOUNCEMENT)
  }, [mainIsRefetchError])

  if (error) {
    return (
      <div style={ISSUES_RESULTS_SPACING_STYLE}>
        <SharedDashboardBlankslate dashboardType="issues" variant="error" />
      </div>
    )
  }

  return (
    <>
      {mainIsRefetchError && (
        <Banner
          variant="warning"
          title="Unable to refresh issues."
          description="Showing previously loaded results."
          primaryAction={
            <Button
              onClick={async () => {
                const result = await refetchMain()
                if (result.isRefetchError) {
                  announce(REFRESH_ERROR_ANNOUNCEMENT)
                } else if (result.isSuccess) {
                  focusSearchInput(searchInputRef)
                }
              }}
            >
              Retry
            </Button>
          }
        />
      )}
      <RepoIssuesBulkJobInfo
        jobId={bulkJobId}
        enabled
        clearJobId={clearBulkJobId}
        refreshIssues={refreshIssues}
        onCompleted={onBulkJobCompleted}
        onError={onBulkJobError}
      />
      {bulkActionError ? (
        <Banner className="mt-3" variant="critical" title={bulkActionError} onDismiss={clearBulkActionError} />
      ) : null}
      {layoutPayload?.firstTimeContributionBanner && (
        <FirstTimeContributionBanner
          banner={layoutPayload.firstTimeContributionBanner}
          searchInputRef={searchInputRef}
        />
      )}
      <SingleSignOnBanner protectedOrgs={orgsRequiringSso} />
      {!isNavigating && (
        <IssueRowsPreheater
          rows={issues}
          listIdentity={JSON.stringify([pathname, fetchedQuery, currentPage])}
          source="IssuesIndex"
          preloadComponents={preloadIssueViewer}
          intentContainerRef={issueListRef}
        />
      )}
      <IssuesListSurface
        response={{...data, pageInfo}}
        issueStatusSearchOptions={issueStatusSearchOptions}
        routeAdapter={{
          scopeIdentity: `repository:${owner}/${repo}`,
          query: fetchedQuery,
          page: currentPage,
          fetch: ({query, page}) => fetchResults({q: query, page}),
          getPageHref,
          getQueryHref,
        }}
        filterActions={filterActions}
        defaultSortingKey={defaultSortingKey}
        separateAgentAssignees
        isLoading={isNavigating}
        issuesOverride={issues}
        beforeList={liveUpdates}
        compactMode={compactMode}
        onCompactModeChange={onCompactModeChange}
        onSelectRow={trackRowSelection}
        onIssueNavigate={trackIssueNavigation}
        containerProps={{ref: issueListRef}}
        emptyListStateOverride={emptyListState}
        selection={selection}
      />
      {notificationStateCommands}
      {showMoreResultsAvailable && <MoreResultsAvailableBanner itemsLabel={itemsLabel} maxItems={maxItems} />}
    </>
  )
}

const MemoizedIssuesIndexPageContent = memo(IssuesIndexPageContent)

function FirstTimeContributionBanner({
  banner,
  searchInputRef,
}: {
  banner: NonNullable<
    NonNullable<
      ReturnType<typeof useRouteQuery<typeof issuesLayoutRoute, 'mainQuery'>>['data']
    >['firstTimeContributionBanner']
  >
  searchInputRef: RefObject<HTMLInputElement | null>
}) {
  const [dismissed, setDismissed] = useState(false)

  const dismiss = useCallback(
    async (url: string, method: 'DELETE' | 'POST') => {
      try {
        const response = await reactFetch(url, {method})
        if (!response.ok) return

        searchInputRef.current?.focus()
        setDismissed(true)
        announce('First-time contribution banner dismissed')
      } catch {
        // Keep the banner visible so the viewer can retry.
      }
    },
    [searchInputRef],
  )

  if (dismissed) return null

  return (
    <FirstTimeContributionBannerDisplay
      repoNameWithOwner={banner.repoNameWithOwner}
      contributingGuidelinesUrl={banner.contributingGuidelinesUrl}
      hasGoodFirstIssueIssues={banner.hasGoodFirstIssueIssues}
      contributeUrl={banner.contributeUrl}
      dismissForThisRepo={() => void dismiss(banner.dismissForRepoUrl, 'DELETE')}
      dismissForAllRepos={() => void dismiss(banner.dismissForAllUrl, 'POST')}
    />
  )
}
